import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MoreVertical, Zap, CalendarClock, CalendarDays, PlayCircle, CheckCircle2 } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { norm } from "@/lib/normalize";
import { formatINR } from "@/lib/currency";
import { formatDate, formatClock } from "@/lib/datetime";

const STATUS_COLORS = {
  scheduled: "bg-[rgba(197,160,89,0.15)] text-gold border-gold-subtle",
  in_service: "bg-[rgba(37,99,235,0.18)] text-blue-300 border-blue-800",
  completed: "bg-[rgba(46,125,50,0.2)] text-green-300 border-green-800",
  cancelled: "bg-[rgba(211,47,47,0.15)] text-red-300 border-red-900",
  "no-show": "bg-[rgba(140,140,140,0.15)] text-gray-300 border-gray-700",
};

// Friendly display labels for statuses that don't read well when just capitalized.
const STATUS_LABELS = {
  in_service: "In Service",
  "no-show": "No-show",
};

const statusLabel = (status) => {
  const key = norm(status);
  return STATUS_LABELS[key] || key;
};

// Add duration (in minutes) to a "HH:mm" start time, returning "HH:mm".
const addMinutes = (startTime, minutes) => {
  const [h, m] = (startTime || "00:00").slice(0, 5).split(":").map(Number);
  const total = h * 60 + m + (Number(minutes) || 0);
  const wrapped = ((total % 1440) + 1440) % 1440;
  const hh = String(Math.floor(wrapped / 60)).padStart(2, "0");
  const mm = String(wrapped % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

export default function Appointments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillAppointment = params.get("appointment") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ appointmentDate: "", startTime: "", endTime: "" });
  const [savingReschedule, setSavingReschedule] = useState(false);

   const goQuickSale = (a) => {
    const params = new URLSearchParams();
    if (a.id) params.set("appointment", a.id);
    if (a.customerId) params.set("customer", a.customerId);
    if (a.appointmentDate) params.set("date", a.appointmentDate);
    if (a.serviceId) params.set("service", a.serviceId);
    if (a.staffId) params.set("staff", a.staffId);
    const url = `/dashboard/quick-sale?${params.toString()}`;
    // eslint-disable-next-line no-console
    //console.log("[QuickSale] navigating to", url, "user role:", norm(user.roleName));
    navigate(url);
  };

  const load = async () => {
    setLoading(true);
    try {
      if (prefillAppointment) {
         const r = await api.get(`/appointments/customer/${prefillAppointment}`);
         setRows(r.data);
      } else{
        const r = await api.get("/appointments");
        setRows(r.data);
      }
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status?status=${status}`);
      toast.success(`Marked ${statusLabel(status)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.message || "Failed to update status");
    }
  };

  const cancel = async (id) => {
    try { await api.delete(`/appointments/${id}`); toast.success("Cancelled"); load(); }
    catch { toast.error("Failed"); }
  };

  const openReschedule = (a) => {
    const start = (a.startTime || "").slice(0, 5);
    const end = a.endTime
      ? a.endTime.slice(0, 5)
      : (start ? addMinutes(start, a.durationMinutes || 60) : "");
    setRescheduleForm({
      appointmentDate: a.appointmentDate || "",
      startTime: start,
      endTime: end,
    });
    setRescheduleAppt(a);
  };

  // Keep endTime in sync with startTime using the appointment's duration
  const onStartTimeChange = (value) => {
    const durationMin = rescheduleAppt?.durationMinutes || 60;
    setRescheduleForm((f) => ({
      ...f,
      startTime: value,
      endTime: value ? addMinutes(value, durationMin) : "",
    }));
  };

  const saveReschedule = async () => {
    if (!rescheduleAppt) return;
    const { appointmentDate, startTime, endTime } = rescheduleForm;
    if (!appointmentDate || !startTime || !endTime) {
      toast.error("Please provide date, start and end time");
      return;
    }
    setSavingReschedule(true);
    try {
      await api.patch(`/appointments/${rescheduleAppt.id}/schedule`, {
        appointmentDate,
        startTime,
        endTime,
      });
      toast.success("Appointment rescheduled");
      setRescheduleAppt(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to reschedule");
    } finally {
      setSavingReschedule(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="appointments-page">
        <div>
          <div className="overline mb-2">Bookings</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
            <CalendarDays className="w-8 h-8 text-indigo-500" /> Appointments
          </h1>
        </div>
      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Guest</TableHead>
              <TableHead className="th-gold">Service</TableHead>
              <TableHead className="th-gold">Stylist</TableHead>
              <TableHead className="th-gold">Date & Time</TableHead>
              <TableHead className="th-gold">Status</TableHead>
              <TableHead className="th-gold text-right">Price</TableHead>
              <TableHead className="th-gold w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-emerald-muted py-8">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-emerald-muted py-8">No appointments yet.</TableCell></TableRow>}
            {rows.map((a) => (
              <TableRow key={a.id} className="border-gold-subtle" data-testid={`appointment-row-${a.id}`}>
                <TableCell className="text-white">{a.customerName || "—"}</TableCell>
                <TableCell className="text-white">{a.serviceName || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{a.staffName || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{formatDate(a.appointmentDate)}, {formatClock(a.startTime)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STATUS_COLORS[norm(a.status)] || ""} capitalize`}>{statusLabel(a.status)}</Badge>
                </TableCell>
                <TableCell className="text-right text-gold">{formatINR(a.price)}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`appointment-menu-${a.id}`} className="text-emerald-muted hover:text-gold p-1"><MoreVertical className="w-4 h-4"/></button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-emerald-surface border-gold-subtle text-white">
                      {(() => {
                        const role = norm(user.roleName);
                        const status = norm(a.status);
                        const canManage = role === "admin" || role === "staff" || role === "receptionist" || role === "manager";
                        const adminOrStaff = role === "admin" || role === "staff";
                        const isScheduled = status === "scheduled";
                        const isInService = status === "in_service";
                        const items = [];
                        // Quick Sale — available while scheduled or in service.
                        if (canManage && (isScheduled || isInService)) {
                          items.push(
                            <DropdownMenuItem key="quick-sale"
                              data-testid={`quick-sale-appt-${a.id}`}
                              onSelect={(e) => { e.preventDefault(); goQuickSale(a); }}
                              className="cursor-pointer">
                              <Zap className="w-4 h-4 text-gold mr-2" /> Quick Sale
                            </DropdownMenuItem>
                          );
                        }
                        // Reschedule — while scheduled or in service.
                        if (canManage && (isScheduled || isInService)) {
                          items.push(
                            <DropdownMenuItem key="reschedule"
                              data-testid={`reschedule-appt-${a.id}`}
                              onSelect={(e) => { e.preventDefault(); openReschedule(a); }}
                              className="cursor-pointer">
                              <CalendarClock className="w-4 h-4 text-gold mr-2" /> Reschedule
                            </DropdownMenuItem>
                          );
                        }
                        // In Service — only from scheduled.
                        if (canManage && isScheduled) {
                          items.push(
                            <DropdownMenuItem key="in-service"
                              data-testid={`in-service-appt-${a.id}`}
                              onSelect={(e) => { e.preventDefault(); setStatus(a.id, "IN_SERVICE"); }}
                              className="cursor-pointer">
                              <PlayCircle className="w-4 h-4 text-blue-400 mr-2" /> In Service
                            </DropdownMenuItem>
                          );
                        }
                        // Mark Completed — once in service.
                        if (canManage && isInService) {
                          items.push(
                            <DropdownMenuItem key="complete"
                              data-testid={`complete-appt-${a.id}`}
                              onSelect={(e) => { e.preventDefault(); setStatus(a.id, "COMPLETED"); }}
                              className="cursor-pointer">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mr-2" /> Mark Completed
                            </DropdownMenuItem>
                          );
                        }
                        // Mark no-show — only from scheduled.
                        if (adminOrStaff && isScheduled) {
                          items.push(
                            <DropdownMenuItem key="no-show" onClick={() => setStatus(a.id, "no-show")}>Mark no-show</DropdownMenuItem>
                          );
                        }
                        // Cancel — while scheduled or in service.
                        if (isScheduled || isInService) {
                          items.push(
                            <DropdownMenuItem key="cancel" data-testid={`cancel-appt-${a.id}`} onClick={() => cancel(a.id)} className="text-red-300 focus:text-red-200">Cancel</DropdownMenuItem>
                          );
                        }
                        if (items.length === 0) {
                          items.push(
                            <DropdownMenuItem key="none" disabled className="text-emerald-muted">No actions available</DropdownMenuItem>
                          );
                        }
                        return items;
                      })()}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!rescheduleAppt} onOpenChange={(o) => { if (!o) setRescheduleAppt(null); }}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reschedule Appointment</DialogTitle>
          </DialogHeader>
          {rescheduleAppt && (
            <div className="space-y-4">
              <div className="text-sm text-emerald-muted">
                {rescheduleAppt.customerName || "—"} · {rescheduleAppt.serviceName || "—"}
              </div>
              <div>
                <Label className="text-emerald-muted">Date</Label>
                <Input
                  type="date"
                  value={rescheduleForm.appointmentDate}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, appointmentDate: e.target.value }))}
                  className="bg-emerald-elev border-gold-subtle mt-1 text-white"
                  data-testid="reschedule-date-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-emerald-muted">Start time</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.startTime}
                    onChange={(e) => onStartTimeChange(e.target.value)}
                    className="bg-emerald-elev border-gold-subtle mt-1 text-white"
                    data-testid="reschedule-start-input" />
                </div>
                <div>
                  <Label className="text-emerald-muted">End time</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.endTime}
                    onChange={(e) => setRescheduleForm((f) => ({ ...f, endTime: e.target.value }))}
                    className="bg-emerald-elev border-gold-subtle mt-1 text-white"
                    data-testid="reschedule-end-input" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRescheduleAppt(null)} className="text-emerald-muted hover:text-white">Cancel</Button>
            <Button onClick={saveReschedule} disabled={savingReschedule} className="bg-gold text-emerald-deep hover:bg-gold/90" data-testid="reschedule-save-btn">
              {savingReschedule ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
